// 확장자를 적는 이유: 단위 테스트(`npm run test:unit`)가 앱 번들 밖(node)에서 이 파일을 그대로 읽는다 — node ESM 은
// 상대 경로의 확장자를 추론하지 않는다. webpack 은 확장자가 있어도 같은 파일을 찾는다.
import { ITEM_STATUS, LINK_STATE } from './deliveryMenuNotes.js'
import { POLL_FAST_MS, POLL_FAST_WINDOW_MS, POLL_SLOW_MS, POLL_WATCH_CAP_MS, pollDelayOf } from './pollSchedule.js'

/**
 * POS 임베드 「배달 메뉴·옵션 품절 관리」 화면의 **판정 로직** — 순수 함수 (결정 브리프 2026-09-03 · D4~D6).
 *
 * 화면(`views/pos/PosDeliverySoldoutView.vue`)은 여기서 판정한 값을 그리기만 한다. 의존은 `deliveryMenuNotes.js`
 * (의존성 없는 어휘·문구 모듈) 하나뿐이라 앱 번들 밖에서 그대로 돈다.
 *
 * 플랫폼 chip 은 `deliveryMenuSync.deliveryMenuChip`·`deliveryOptionChip` 이 만든다(SSOT). 그 함수는
 * `@/mixins/utilMixin` 을 끌어와 번들 밖에서 해석되지 않으므로, 행을 만드는 함수가 chip 생성기를 **주입받고**
 * 판정은 그 결과(`chip.tagType`)만 읽는다.
 *
 * 어휘: 시안(v6)의 상태 8종은 쓰지 않는다. `ITEM_STATUS`·`LINK_STATE`·`unlinkedReason`·`match` 가 전부다.
 */

export const SOLDOUT_ENTITY = Object.freeze({ MENU: 'menu', OPTION: 'option' })

/** 상태 필터 값 — 라벨은 화면이 `$t` 로 붙인다 */
export const SOLDOUT_STATUS_FILTER = Object.freeze({
  ALL: 'all',
  SELLING: 'selling',
  SOLD_OUT: 'soldout',
  IN_PROGRESS: 'processing',
  ATTENTION: 'issue',
})

/** 플랫폼 보기·옵션그룹 필터의 「전체」 값 */
export const SOLDOUT_FILTER_ALL = 'all'

// `OPTION_MATCH`(옵션 항목 지목 결과) 어휘는 `deliveryMenuNotes.js` 에 있다 — 그 낱말을 읽는 곳이
// `deliveryMenuSync.js`(chip)·`PosSoldoutConfirmDialog`(부기)·이 파일 셋이라, 어휘의 정본은
// 셋이 다 의존할 수 있는 모듈 하나여야 한다.

/**
 * 진행 드로어 폴링 (D5) — **간격·상한 숫자는 `pollSchedule.js` 한 곳**이다(고객 가져오기도 같은 간격을 쓴다). 이 화면의 옛
 * 이름은 그 값을 다시 내보낸 것이다(사본 아님).
 *
 * 상한이 「반영이 끝나는 시간」이 아니라 「사람이 서서 보는 시간」인 이유는 그 파일이 적는다 — 실측(2026-09-03 dev)에서 저장부터
 * 배민 반영까지 **7분**이 걸렸다(실행자가 로그인을 복구하고 관측 회차를 도는 동안 순서를 기다린다). 이 화면은 상한에서 멈추고
 * 마지막 확인 시각과 [다시 확인] 을 보인다. **조회가 곧 서버의 지연 재계획**이라 간격을 더 줄이지 않는다(어드민 가이드 §1).
 */
export {
  POLL_FAST_MS as PROGRESS_POLL_INTERVAL_MS,
  POLL_SLOW_MS as PROGRESS_SLOW_POLL_INTERVAL_MS,
  POLL_FAST_WINDOW_MS as PROGRESS_FAST_POLL_WINDOW_MS,
  POLL_WATCH_CAP_MS as PROGRESS_TIMEOUT_MS,
}

/** 이번 회차의 폴링 간격 — 시작 뒤 30초 안이면 촘촘히, 그 뒤로는 느슨하게(`pollSchedule.pollDelayOf` 그대로) */
export const progressPollDelay = pollDelayOf

/** 대상 플랫폼 하나의 진행 판정 어휘 ([progressStateOf]) */
export const PROGRESS_STATE = Object.freeze({
  WAITING: 'waiting',
  IN_PROGRESS: 'progress',
  DONE: 'done',
  FAILED: 'failed',
  BLOCKED: 'blocked',
})

/**
 * 진행 회차의 국면 — 화면의 `progress.phase` 어휘. 저장 중 → 확인 중 → 종결(전부 비진행 종결) 또는 시간초과(상한 초과 —
 * 규약이 정상 경로로 못박은 값). 「방금 보냈다」가 사실인 창은 [progressLive] 가 답한다.
 */
export const PROGRESS_PHASE = Object.freeze({
  SAVING: 'saving',
  POLLING: 'polling',
  SETTLED: 'settled',
  TIMEOUT: 'timeout',
  /** 401/403 으로 확인을 멈췄다 (11R P4) — 다시 확인하지도 재저장하지도 않는다 */
  TERMINAL: 'terminal',
})

/**
 * 이 화면의 요청 공통 옵션.
 *
 * - `showLoading:false` — 전역 스피너 대신 화면이 자기 자리에서 로딩을 말한다(3초 폴링에 전역 스피너가 깜빡이면 안 된다).
 * - `showErrorMessage:false` (§16.175) — 페이지 안 안내·건별 결과가 이 화면의 대체 동작이다. 인터셉터의 빨간 토스트가
 *   함께 뜨면 한 조작에 화면이 두 말을 한다.
 *
 * 403 로그아웃 옵트아웃은 여기 없다 — 그 판정은 라우트 축이 한다(`plugins/http/policy.js`, D2).
 */
export const POS_SOLDOUT_REQUEST_CONFIG = Object.freeze({ showLoading: false, showErrorMessage: false })

/** 진행 중 = 어느 플랫폼이든 마지막 작업이 이 집합 안 (D5 정의) */
export const IN_PROGRESS_STATUSES = Object.freeze([
  ITEM_STATUS.WAITING_EXECUTOR,
  ITEM_STATUS.WAITING_AUTH,
  ITEM_STATUS.QUEUED,
  ITEM_STATUS.CLAIMED,
  ITEM_STATUS.EXECUTING,
])

/** 실패 카드를 띄우는 종결 상태 (D5) — **재저장이 답인 축** */
export const FAILED_STATUSES = Object.freeze([ITEM_STATUS.FAILED, ITEM_STATUS.OUTCOME_UNKNOWN, ITEM_STATUS.EXPIRED])

/**
 * 재저장으로 풀리지 않는 종결 상태 (D7 · appserver 검증).
 *
 * 드리프트가 선 링크(`TARGET_CONFLICT`)는 계획기가 `DRIFT_BLOCKED` 로 막으므로 **같은 값을 다시 저장해도 새 작업이
 * 서지 않는다.** 답은 어드민의 「값 차이 해결」이다. 이 화면은 POS 안에서 열려 그 화면으로 보낼 수 없으므로,
 * 실패와 섞지 않고 따로 말한다(재저장 대상에서 뺀다).
 */
export const BLOCKED_STATUSES = Object.freeze([ITEM_STATUS.DRIFT_BLOCKED])

/**
 * 「확인 필요」의 축 (D5 정의 · 규약 §16.304③ · 9R R1) — [platformNeedsAttention] 이 **링크 사실의 any-fold** 로 본다.
 *
 * - 링크 head 가 실패·막힘([FAILED_STATUSES] ∪ [BLOCKED_STATUSES])인 링크가 **대표 후보 상계**([representativeCandidates])
 *   안에 하나라도 — **대표 chip 의 톤이 아니다**. 대표가 중립 종결(`SUPERSEDED` 등)이면 다른 링크의 `FAILED` 가 톤에서
 *   사라지므로 head 를 직접 본다(9R 리뷰). 상계를 두는 이유(10R R11): 정책이 꺼져 아무것도 나가지 않는 링크의 옛
 *   `EXPIRED` 가 행을 영구히 「확인 필요」로 만들면 안 된다 — 사장님이 할 일이 없는 축이다.
 * - 링크 축 셋(`linkState ∈ {ORPHAN, AMBIGUOUS, STALE}` · `placementIncomplete` · `unlinkedReason === NEVER_IMPORTED`)이
 *   **전체 링크** 중 하나라도 — 범위 밖 링크가 말해야 할 사실이다. 링크 상태의 원본은 [linkStatesOf] — 옵션 축은 서버가
 *   접어 내리면서 링크마다의 실효 상태를 `linkStates` 로 함께 내리므로(접힌 `linkState` 는 `LINKED` 가 하나라도 있으면
 *   `LINKED` 라 죽은 형제 링크를 가린다).
 * - 품절 축 드리프트([platformSoldOutDrift]) · 대표 chip 의 톤(warning|danger).
 *
 * **`MENU_ABSENT` 는 넣지 않는다** (2026-09-03 dev 실측). 「배달앱에 이 메뉴 없음」은 그 앱에서 안 파는 메뉴의
 * **정상이고 영구적인** 상태라 사장님이 할 일이 없다. 넣었더니 dev 매장 13 에서 166건 중 129건이 그 하나 때문에
 * 「확인 필요」가 됐다(진짜 링크 문제는 35건) — 필터가 전부를 가리키면 아무것도 안 가리키는 것과 같다.
 * 남긴 `NEVER_IMPORTED` 는 할 일이 있는 축이다(그 매장에서 「메뉴 가져오기」 1회).
 */
const ATTENTION_UNLINKED_REASONS = Object.freeze(['NEVER_IMPORTED'])
const ATTENTION_LINK_STATES = Object.freeze([LINK_STATE.ORPHAN, LINK_STATE.AMBIGUOUS, LINK_STATE.STALE])
const ATTENTION_TAG_TYPES = Object.freeze(['warning', 'danger'])

export function isInProgressItem(item) {
  return !!item && IN_PROGRESS_STATUSES.indexOf(item.status) > -1
}

export function isFailedItem(item) {
  return !!item && FAILED_STATUSES.indexOf(item.status) > -1
}

export function isBlockedItem(item) {
  return !!item && BLOCKED_STATUSES.indexOf(item.status) > -1
}

/** 세션이 끝났거나 권한이 없다 — 페이지 안 안내로 답하는 두 상태 코드 (`HttpError.status`) */
export function isAuthFailureStatus(status) {
  return status === 401 || status === 403
}

export function menuRowKey(smenuCd) {
  return `menu:${smenuCd}`
}

export function optionRowKey(smenuOptCd) {
  return `option:${smenuOptCd}`
}

const searchable = (parts) =>
  parts
    .filter((part) => part != null && part !== '')
    .join(' ')
    .toLowerCase()

// ────────────────────────────────────────────────────────────────────
// 링크 여럿 → 플랫폼 한 칸 (브리프 2026-09-04 D-A·D-D·D-E · 규약 §16.304②)
//
// 서버의 `platforms[]` 는 **링크마다** 한 건이다(한 매장이 같은 배달앱에 샵을 여럿 가질 수 있다). 화면은 플랫폼당
// 칸 하나라 접어야 하는데, 6~8라운드가 같은 자리에서 세 번 뒤집힌 원인은 규칙의 세부가 아니라 **모델**이었다 —
// ① revision 순서를 `itemId` 로 썼고 ② 「현재 스코프」를 이력 휴리스틱(최근 회차)으로 추정했고 ③ 대표 하나에서
// 모든 축을 읽었다. 그래서 여기서는 축마다 자기 접기를 갖고(any / max / 대표), 대표는 revision 과 반영 범위로 고른다.
// 이 자리를 다시 「규칙 한 줄」로 고치지 않는다 — 표를 고쳐야 하면 규약 §16.304② 의 표와 함께 고친다.
// ────────────────────────────────────────────────────────────────────

/**
 * **revision 순서** — item 의 순서는 `(intentId, itemId)` 사전식이다 (D-A).
 *
 * 도메인(`DeliveryMenuRevision`)이 「`intent_id` 단조성이 곧 revision 순서다」라고 못박아 두었다 — 계획 유실 복구는
 * **옛 intent 를 나중에 materialize** 하므로 `itemId` 가 크다고 새 작업이 아니다. `intentId` 가 없는 옛 서버 응답에서만
 * `itemId` 로 폴백한다.
 *
 * @param a `{intentId, itemId}` — `lastItem` 또는 [revisionOf] 의 결과. `null` 은 「없음」이고 무엇보다 옛것이다
 * @returns 음수·0·양수 — `a` 가 `b` 보다 옛것·같음·새것
 */
export function compareRevision(a, b) {
  if (!a || !b) return !a && !b ? 0 : !a ? -1 : 1
  if (a.intentId != null && b.intentId != null && a.intentId !== b.intentId) return a.intentId < b.intentId ? -1 : 1
  if (a.itemId === b.itemId) return 0
  return a.itemId < b.itemId ? -1 : 1
}

/** item → 스냅샷에 남기는 revision `{intentId, itemId}`. 없으면 `null` */
export function revisionOf(item) {
  return item ? { intentId: item.intentId != null ? item.intentId : null, itemId: item.itemId } : null
}

/**
 * 링크 하나의 스냅샷 키 — `linkId` 가 있으면 그것, 없으면 플랫폼 (D-F).
 * 옵션 축은 서버가 플랫폼당 하나로 접어 내리므로 `linkId` 가 없고 키가 플랫폼이다.
 */
export function linkKeyOf(status) {
  return status && status.linkId != null ? String(status.linkId) : status.platform
}

/**
 * **이 링크로 이번 저장이 나가는가** — 반영 범위의 링크 술어 (D-E `inScope`). 메뉴·옵션 축이 같은 술어다:
 * `LINKED` 이고 정책이 꺼지지 않았고(`syncEnabled !== false` — 축이 없는 옛 응답은 켜진 것으로 본다, [deliveryMenuChip]
 * 과 같은 눈) 그 플랫폼이 그 축을 지원한다(`fieldSupported !== false` — 메뉴 축에는 없는 축이라 통과한다).
 * 지목(`match`)은 조건이 아니다 — 옵션 쓰기의 실행 대상은 그룹이라 항목을 못 지목해도 그룹 값이 나간다(§16.304②).
 */
export function linkInScope(status) {
  return (
    !!status &&
    status.linkState === LINK_STATE.LINKED &&
    status.syncEnabled !== false &&
    status.fieldSupported !== false
  )
}

/**
 * **한 status 가 품은 링크 상태들** — 링크 축 any-fold 의 원본 (9R R1).
 *
 * 옵션 축은 서버가 플랫폼당 하나로 접어 내리면서 링크마다의 실효 `linkState` 를 `linkStates`(링크 순서대로)로 함께
 * 내린다 — 접힌 `linkState` 만 보면 둘째 샵의 `STALE`·`ORPHAN` 이 어디에도 안 나타난다. 그 축이 없는 응답(메뉴 축의
 * 링크 한 건 · 옛 서버)은 `linkState` 하나가 곧 목록이고, 링크가 없으면(`linkState == null`) 빈 목록이다 — 서버 wire 의
 * `[]` 와 같은 모양이라 접힌 `linkStates` 가 `[null]` 로 갈리지 않는다(10R R11 보강).
 */
export function linkStatesOf(status) {
  if (status && Array.isArray(status.linkStates)) return status.linkStates
  return status && status.linkState != null ? [status.linkState] : []
}

/**
 * 링크 상태의 접기 — 서버 `DeliveryMenuOptionStatus.foldLinkState` 와 **같은 네 갈래** (D-E):
 * 비면 `null` · 전부 같으면 그 값 · `LINKED` 가 하나라도 있으면 `LINKED`(그 플랫폼에는 나간다) · 아니면 첫 링크.
 */
export function foldLinkState(states) {
  const list = states || []
  if (list.length === 0) return null
  if (list.every((s) => s === list[0])) return list[0]
  return list.indexOf(LINK_STATE.LINKED) > -1 ? LINK_STATE.LINKED : list[0]
}

/**
 * 대표의 우선순위 — chip 한 칸에 무엇을 적을 것인가 (D-D). 큰 것이 이긴다:
 * 3 진행 중([IN_PROGRESS_STATUSES]) · 2 성공 아닌 종결 전부(`FAILED`·`OUTCOME_UNKNOWN`·`EXPIRED`·`DRIFT_BLOCKED`·
 * `SUPERSEDED`·`BLOCKED_LINK`·`NOT_LINKED`·`DISABLED`·`UNSUPPORTED`·…) · 1 이력 없음(「반영 이력 없음」 — 클라이언트만) ·
 * 0 `SUCCEEDED`. status 는 서버가 `EXPIRED` 파생을 반영해 내린 실효값이다.
 *
 * 진행 중이 실패보다 앞서는 이유: 「지금 무언가 진행 중인가」가 「다시 눌러도 되는가」를 정하고, 진행이 끝나면 실패가
 * 그 자리에서 드러난다. 실패가 진행을 가리면 행이 풀려 **중복 저장**이 난다. 계획종결 status(`NOT_LINKED` 등)를 따로
 * 빼지 않는다 — revision([compareRevision])과 반영 범위([foldPlatformStatus] 의 후보)가 이미 가른다.
 */
function headRank(head) {
  if (!head) return 1
  if (isInProgressItem(head)) return 3
  return head.status === ITEM_STATUS.SUCCEEDED ? 0 : 2
}

/** 후보 링크들 중 대표 링크 — rank 가 큰 것, 같으면 head 의 revision 이 최신인 것. 비면 `null` */
export function representativeLink(links) {
  return (links || []).reduce((best, st) => {
    if (!best) return st
    const byRank = headRank(st.lastItem) - headRank(best.lastItem)
    return byRank > 0 || (byRank === 0 && compareRevision(st.lastItem, best.lastItem) > 0) ? st : best
  }, null)
}

/**
 * **대표 후보의 상계** — 플랫폼 한 칸에서 「지금」을 말할 자격이 있는 링크들 (D-E · 10R R11).
 *
 * 반영 범위([linkInScope]) 안의 링크, 없으면 `LINKED` 링크, 없으면 전부 — 거기에 진행 중인 head 를 가진 링크는 범위와
 * 무관하게 남는다(실행 중인 것은 사실이다 — D-C 의 예외). 대표(chip)와 「확인 필요」의 실패·막힘 any 가 **같은 상계**를
 * 본다: 정책이 꺼져 아무것도 나가지 않는 링크의 옛 `EXPIRED` 가 후보 밖이면 실패 축에서도 밖이다 — 사장님이 할 일이
 * 없는 축은 「확인 필요」가 아니다(`MENU_ABSENT` 교훈). 링크 상태 축(ORPHAN·AMBIGUOUS·STALE·placementIncomplete·
 * NEVER_IMPORTED)은 이 상계를 지나지 않는다 — 그것이 바로 범위 밖 링크가 말해야 할 사실이다.
 */
export function representativeCandidates(links) {
  const list = links || []
  const scoped = list.filter(linkInScope)
  const linked = list.filter((st) => st.linkState === LINK_STATE.LINKED)
  const pool = scoped.length > 0 ? scoped : linked.length > 0 ? linked : list
  const inFlight = list.filter((st) => isInProgressItem(st.lastItem) && pool.indexOf(st) < 0)
  return pool.concat(inFlight)
}

/**
 * **한 플랫폼의 링크 여럿 → 접힌 status 하나** — 축마다 다른 접기 (D-E · 규약 §16.304② 표).
 *
 * | 축 | 접기 |
 * |---|---|
 * | `linkStates` | 링크마다의 실효 상태 목록([linkStatesOf] 를 이어 붙임) — 접기의 **입력**이자 「확인 필요」 링크 축의 원본. |
 * |  | 옵션 축은 서버 값 그대로 보존된다(9R R1) |
 * | `linkState` | [foldLinkState]`(linkStates)` — 서버 옵션 축과 같은 네 갈래, 같은 입력 |
 * | `unlinkedReason` | 링크가 없을 때만(첫 항목 값) |
 * | `syncEnabled` | any(`!== false`) — 메뉴 축은 (매장, 메뉴, 플랫폼) 단위라 링크 간 같다 |
 * | `fieldSupported`·`match`·`observed` | 플랫폼 단위 그대로(옵션 축은 서버가 이미 접어 내린다) |
 * | `placementIncomplete` | any |
 * | `sharedStoreCount` | max |
 * | `lastItem`·`drift` | **대표 링크**의 것([representativeLink]). 후보는 [representativeCandidates] — 반영 범위 안의 |
 * |  | 링크, 없으면 `LINKED`, 없으면 전부, + 진행 중인 head 의 링크. 칸의 `candidates` 가 같은 목록을 싣는다(10R R11) |
 *
 * 결과는 서버 옵션 축 응답(`DeliveryMenuOptionPlatformStatus`)과 같은 모양이라 chip 생성기(`deliveryMenuChip`·
 * `deliveryOptionChip`)에 그대로 넘긴다. 링크 식별(`linkId`·`shopNo`·`shopNm`)은 접히면 뜻이 없어 비운다.
 * 접기 전 링크 목록은 칸의 `links` 가 보존한다 — 진행·「확인 필요」·반영 범위 같은 any 축은 그것을 본다.
 */
export function foldPlatformStatus(links) {
  const list = links || []
  const first = list[0] || {}
  const representative = representativeLink(representativeCandidates(list)) || first
  // 접기 입력을 한 번 만들어 `foldLinkState` 와 `linkStates` 가 같은 목록을 본다 — 서버 `optionStatus` 와 같은 규율
  const linkStates = list.reduce((acc, st) => acc.concat(linkStatesOf(st)), [])
  const linkState = foldLinkState(linkStates)
  return {
    ...first,
    linkId: null,
    shopNo: null,
    shopNm: null,
    linkStates,
    linkState,
    unlinkedReason: linkState == null ? first.unlinkedReason || null : null,
    syncEnabled: list.some((st) => st.syncEnabled !== false),
    lastItem: representative.lastItem || null,
    drift: representative.drift || null,
    placementIncomplete: list.some((st) => st.placementIncomplete === true),
    sharedStoreCount: list.reduce((max, st) => Math.max(max, st.sharedStoreCount || 0), 0),
  }
}

/**
 * 링크 축 응답을 플랫폼 축으로 묶는다 — 표시 순서는 처음 본 순서 그대로. 옵션 축은 서버가 이미 플랫폼당 하나로
 * 접어 내리므로 링크 하나짜리 묶음이 되고, 같은 길([platformEntry])을 지난다 — 접기 규칙이 두 벌이 되지 않는다.
 */
function groupByPlatform(statuses) {
  const order = []
  const byPlatform = {}
  ;(statuses || []).forEach((status) => {
    const key = status.platform
    if (!byPlatform[key]) {
      byPlatform[key] = []
      order.push(key)
    }
    byPlatform[key].push(status)
  })
  return order.map((platform) => ({ platform, links: byPlatform[platform] }))
}

/**
 * 플랫폼 한 칸 — 접힌 status 와 chip, 그리고 링크 집합에서 파생한 any/max 축 (D-E).
 *
 * `chipOf` 는 `deliveryMenuChip`(인자 하나) 또는 `deliveryOptionChip`(옵션 이름을 더 받는다). 판정이 읽는 것은
 * `chip.tagType` 뿐이다. `sharedStoreCount` 는 배민만 유효하다(D8).
 */
function platformEntry(platform, links, chipOf, chipName) {
  const status = foldPlatformStatus(links)
  const base = { platform, status, links }
  return {
    ...base,
    // 대표 후보의 상계 — 「확인 필요」의 실패·막힘 any 가 대표(chip)와 같은 목록을 본다 (10R R11)
    candidates: representativeCandidates(links),
    chip: chipOf(status, chipName),
    observedAt: newestLinkObservedAt(links),
    observed: status.observed || null,
    soldOutDrift: platformSoldOutDrift(base),
    // **연결이 있는가·나갈 곳인가는 어느 링크든** 참이면 참이다 — 대표 하나로 줄이면 다른 샵으로 나가는 저장을 숨긴다
    hasLink: platformHasLink(base),
    inScope: platformInScope(base),
    sharedStoreCount: platform === 'BAEMIN' ? status.sharedStoreCount : 0,
  }
}

/**
 * 메뉴 한 줄 → 화면 행.
 *
 * `statusRow` 는 `GET /api/delivery-menu/status` 의 `menus[]` 한 건이고 `null` 이면 아직 안 읽었다(플랫폼 칸이 빈 채로
 * 「상태를 읽는 중」). 반영 범위는 [linkInScope] — `LINKED` 이고 정책이 켜진 링크가 하나라도 있는 플랫폼.
 *
 * @param {(status: object) => object} chipOf `deliveryMenuChip`
 */
export function buildMenuRow(menu, statusRow, chipOf) {
  const platforms = groupByPlatform((statusRow && statusRow.platforms) || []).map(({ platform, links }) =>
    platformEntry(platform, links, chipOf)
  )
  const name = menu.smenuNmLan || menu.smenuNm || ''
  return {
    key: menuRowKey(menu.smenuCd),
    entity: SOLDOUT_ENTITY.MENU,
    id: menu.smenuCd,
    name,
    imagePath: menu.smenuImg || '',
    subtitle: menu.categorysNm || '',
    price: menu.price != null ? menu.price : null,
    groupKey: null,
    menus: [],
    searchText: searchable([name, menu.smenuNm, menu.categorysNm]),
    stopSelling: !!menu.stopSelling,
    statusLoaded: !!statusRow,
    platforms,
    // **두 물음을 각각 한 번 파생한다** (D-F) — 「연결 필요」 배지는 `hasLink`(연결이 있는가), 확인창의
    // 「오더엔에만 저장됩니다」는 `hasTarget`(나갈 곳이 있는가). 고를 수 있는가는 [rowSelectable] 이 따로 답한다.
    hasLink: platforms.some((p) => p.hasLink),
    hasTarget: platforms.some((p) => p.inScope),
  }
}

/**
 * 옵션그룹 한 건(`GET /api/delivery-menu/option-status` 의 `groups[]`) → 항목마다 화면 행.
 *
 * - `useYn='N'`(삭제·사용 안 함) 항목은 싣지 않는다 — 그 축은 숨김이고 이 화면의 범위 밖이다(D9).
 * - 서버가 플랫폼당 한 줄로 접어 내리므로 칸마다 링크 하나짜리 묶음이다 — 접기·파생은 메뉴 축과 같은 길이다.
 * - 반영 범위는 [linkInScope] — `LINKED` 이고 정책이 켜졌고(`syncEnabled`, D-G) 그 플랫폼이 `optionSoldOut` 을 지원.
 *   ★ **지목(`match`)은 조건이 아니다** (2026-09-03 리뷰 · 규약 §16.304② 정정): 옵션 쓰기의 실행 대상은 **그룹**
 *   (`scope_tp=OPTION_GROUP`)이라 항목을 이름으로 지목하지 못해도 그 그룹에 링크가 있으면 서버는 배달앱으로
 *   내보낸다. `match === EXACT` 를 범위에 넣으면 리얼(옵션 축 관측이 아직 0건)에서 확인창이 「오더엔에만
 *   저장됩니다」라고 거짓으로 단정한다. 지목 실패는 **부기**가 말한다(chip 이 이미 한다).
 * - 배달앱 반영 대상 있음(`hasTarget`) 은 **고를 수 있는가와 다른 물음이다** — 나갈 곳이 없어도 오더엔 저장은
 *   뜻을 가지므로 확인창이 「오더엔에만 저장됩니다」로 말한다(2026-09-03 리뷰).
 *
 * @param {(status: object, optionName: string) => object} chipOf `deliveryOptionChip`
 */
export function buildOptionRows(group, chipOf) {
  const menus = ((group && group.menus) || []).map((m) => m && m.smenuNm).filter(Boolean)
  return ((group && group.items) || [])
    .filter((item) => item && item.useYn !== 'N')
    .map((item) => {
      const platforms = (item.platforms || []).map((status) =>
        platformEntry(status.platform, [status], chipOf, item.name)
      )
      return {
        key: optionRowKey(item.smenuOptCd),
        entity: SOLDOUT_ENTITY.OPTION,
        id: item.smenuOptCd,
        name: item.name || '',
        subtitle: group.name || '',
        price: item.price != null ? item.price : null,
        groupKey: String(group.smenuOptTpCd),
        menus,
        searchText: searchable([item.name, group.name].concat(menus)),
        stopSelling: !!item.stopSelling,
        statusLoaded: true,
        platforms,
        hasLink: platforms.some((p) => p.hasLink),
        hasTarget: platforms.some((p) => p.inScope),
      }
    })
}

// ── 플랫폼 칸의 any 축 — 전부 **링크 집합**(`entry.links`) 위에서 답한다 (D-E) ──

const linksOf = (entry) => (entry && entry.links) || []

/**
 * 목록 → 키 맵, **선형**으로 (12R P23). `reduce((acc, x) => ({ ...acc, [k]: v }), {})` 는 원소마다 맵 전체를 복사해
 * O(N²) 다 — 판정이 computed 가 되면서 렌더마다 재조립되므로 행 1000 에서 눈에 띈다(리뷰어 실측 ~156ms/렌더).
 * 지역 accumulator 를 변이할 뿐 인자는 건드리지 않는다 — 밖에서는 순수하다. 뒤 원소가 같은 키를 덮는다.
 */
export function indexBy(list, keyOf, valueOf) {
  const acc = {}
  ;(list || []).forEach((x) => {
    acc[keyOf(x)] = valueOf ? valueOf(x) : x
  })
  return acc
}

/**
 * **그 링크의 품절 값이 오더엔과 갈라졌는가** (2026-09-03 리뷰 지적).
 *
 * `drift` 는 「오더엔 목표와 플랫폼 관측이 다르다」이고 `fields` 가 어느 축인지 말한다. 품절 축의 드리프트는
 * 이 화면이 존재하는 이유 그 자체다 — 오더엔은 품절인데 배달앱은 팔고 있다는 뜻이라 **손님이 품절된 메뉴를
 * 주문한다.** 그런데 chip 은 마지막 작업만 보므로, 그 뒤에 플랫폼에서 값이 되돌려졌으면 초록 「반영 완료」가
 * 그대로 남아 화면이 아무 말도 하지 않는다. 다른 축(가격·이름)의 드리프트까지 경보로 올리면 거짓 경보가
 * 되므로 **품절 축만** 본다.
 */
export function linkSoldOutDrift(status) {
  const drift = status && status.drift
  if (!drift || drift.state !== 'DRIFT') return false
  return (drift.fields || []).indexOf('stopSelling') > -1
}

/** 플랫폼 한 칸 — 어느 링크든 품절 값이 갈라졌으면 참 */
export function platformSoldOutDrift(entry) {
  return linksOf(entry).some(linkSoldOutDrift)
}

/** 연결이 있는가 — 어느 링크든 `LINKED` 면 참. 「연결 필요」 배지의 축 */
export function platformHasLink(entry) {
  return linksOf(entry).some((st) => st.linkState === LINK_STATE.LINKED)
}

/** 나갈 곳인가 — 어느 링크든 [linkInScope] 면 참. 확인창 반영 범위·`withoutTargetCount`·진행 대상의 축 */
export function platformInScope(entry) {
  return linksOf(entry).some(linkInScope)
}

/** 진행 중 — 어느 링크든 head 가 진행 집합 안이면 참. **대표가 아니라 any** (D-E) */
export function platformInProgress(entry) {
  return linksOf(entry).some((st) => isInProgressItem(st.lastItem))
}

/** 칸의 대표 후보 상계 — 행 빌더가 실어 둔 목록, 없으면 같은 규칙으로 파생한다([representativeCandidates]) */
const candidatesOf = (entry) => (entry && entry.candidates) || representativeCandidates(linksOf(entry))

/**
 * 「확인 필요」 (D5 정의 · 규약 §16.304③ · 9R R1 · 10R R11) — 링크 사실의 any-fold, 축마다 자기 상계 위에서:
 * - 실패·막힘([isFailedItem]·[isBlockedItem])은 **대표 후보**([candidatesOf]) 안의 링크 head 만 — 정책이 꺼져 아무것도
 *   나가지 않는 링크의 옛 실패는 후보 밖이라 세지 않는다(사장님이 할 일이 없는 축).
 * - `linkState ∈ {ORPHAN, AMBIGUOUS, STALE}`([linkStatesOf] 위에서) · `placementIncomplete` · `unlinkedReason === NEVER_IMPORTED`
 *   는 **전체 링크** — 범위 밖 링크가 말해야 할 사실이다.
 * - 거기에 품절 축 드리프트와 대표 chip 의 톤(warning|danger). 축의 뜻은 [ATTENTION_UNLINKED_REASONS] 머리주석.
 */
export function platformNeedsAttention(entry) {
  if (!entry) return false
  const tagType = entry.chip && entry.chip.tagType
  return (
    platformSoldOutDrift(entry) ||
    ATTENTION_TAG_TYPES.indexOf(tagType) > -1 ||
    candidatesOf(entry).some((st) => isFailedItem(st.lastItem) || isBlockedItem(st.lastItem)) ||
    linksOf(entry).some(
      (st) =>
        linkStatesOf(st).some((state) => ATTENTION_LINK_STATES.indexOf(state) > -1) ||
        ATTENTION_UNLINKED_REASONS.indexOf(st.unlinkedReason) > -1 ||
        st.placementIncomplete === true
    )
  )
}

export function rowInProgress(row) {
  return ((row && row.platforms) || []).some(platformInProgress)
}

export function rowNeedsAttention(row) {
  return ((row && row.platforms) || []).some(platformNeedsAttention)
}

/**
 * **방금 저장한 행은 새 작업이 보일 때까지 다시 누를 수 없다** (2026-09-04 리뷰 지적).
 *
 * 저장은 성공했는데 계획기가 아직 item 을 내기 전이면 `lastItem` 은 여전히 **이전** 작업이라
 * `rowInProgress` 가 거짓이다. 그 창에서 같은 행을 또 누르면 같은 값이 한 번 더 나가고, 진행 판정의
 * 기준점(저장 전 스냅샷)도 덮인다. 화면이 「방금 보냈다」를 아는 유일한 자리는 진행 상태이므로 여기서 본다 —
 * `pendingKeys` 는 진행 대상 중 어느 링크든 아직 `WAITING` 인 행의 키다(D-F).
 */
export function rowAwaitingNewItem(row, pendingKeys) {
  return !!row && !!pendingKeys && pendingKeys.indexOf(row.key) > -1
}

/**
 * 체크할 수 있는가 (D5·D-F·14R P46) = 되읽는 중이 아니고 · 상태를 읽었고 · 어느 링크도 진행 중이 아니고 · 방금 보낸 행이 아니다.
 *
 * `opts.reloading` 은 **뷰의 사실**(전체 되읽기가 도는 중)이다. 새 요청이 도는 동안 옛 스냅샷은 권위가 없어 잠그되, 스냅샷을
 * 지워서 잠그지 않는다 — 지우면 세대·순번 검사에 걸린 되읽기가 되돌릴 자리가 없어 화면이 오류 없이 잠긴 채 남는다
 * (13R P29 의 보정). 「권위 없음」은 선폐기가 아니라 입력이고, 커밋은 원자다.
 *
 * @param opts `{reloading}` — 없으면 되읽는 중이 아니다
 */
export function rowSelectable(row, pendingKeys, opts) {
  if (opts && opts.reloading === true) return false
  if (rowAwaitingNewItem(row, pendingKeys)) return false
  // **배달앱 대상이 없어도 고를 수 있다 — 저장의 1차 뜻은 오더엔이다** (2026-09-03 리뷰 지적).
  //
  // 종전에는 `hasTarget` 을 함께 봤는데, 그 축은 메뉴에서 상수 `true` 이고 옵션에서만 「EXACT 로 지목된 링크가
  // 있는가」였다. 그래서 같은 저장이 축에 따라 되고 안 되고가 갈렸고, **리얼에서는 옵션 축 관측이 아직 한 번도
  // 돌지 않아 EXACT 가 0건이라 옵션 탭이 통째로 잠겼다.** 품절은 배달앱에 나가기 전에 오더엔의 POS·QR 주문을
  // 먼저 막는 값이므로, 나갈 곳이 없다는 것은 「못 고른다」가 아니라 **확인창이 말해야 할 사실**이다
  // (`posSoldoutNoTargetNote` — 메뉴 축이 이미 그렇게 한다).
  // **반영 범위를 모르는 행은 고를 수 없다** (2026-09-03 리뷰 지적). 목록(`/smenu/store`)은 상태
  // (`/api/delivery-menu/status`)보다 먼저 도착하고, 상태 조회가 실패하면 영영 오지 않는다. 그 사이의 행은
  // `platforms` 가 비어 있어 확인창이 「배달앱 반영 대상 없음」이라고 **거짓으로** 말하는데, 저장은 그대로 나가
  // 실제로는 연결된 배달앱에 반영된다. 고를 수 있는 조건에 「상태를 읽었다」를 넣어 그 창을 닫는다.
  return !!row && row.statusLoaded === true && !rowInProgress(row)
}

/** 이번 저장이 나가는 플랫폼 칸들 — [outboundScopeOf] 의 재료 */
export function scopePlatformsOf(row) {
  return ((row && row.platforms) || []).filter((p) => p.inScope === true)
}

/**
 * **반영 범위의 정본** (14R P55) — 이번 저장이 나가는 것에 관한 사실 하나. 지문([saveFingerprintOf])·확인창 요약
 * ([scopeSummary])·회차 대상([runOf])·확인창의 문구 조건이 전부 여기서 파생한다. 각자 행에서 다시 읽으면 축이 하나씩
 * 빠진다 — 지문에 링크 id·옵션 그룹이 없었고, 공유 매장 수는 범위 밖 링크까지 셌다(원장 `CONFIRM-SHARED-ROWS-OUT-OF-SCOPE`).
 *
 * @returns `{entity, groupKey, inScopeLinkIds, platforms, sharedStoreCount}` — 링크 키([linkKeyOf])는 정렬, 플랫폼은
 *   처음 본 순서, 공유 매장 수는 **범위 안** 링크의 max(배민만 — D8)
 */
export function outboundScopeOf(row) {
  const entries = scopePlatformsOf(row)
  const links = entries.reduce(
    (acc, p) =>
      acc.concat(
        linksOf(p)
          .filter(linkInScope)
          .map((status) => ({ platform: p.platform, status }))
      ),
    []
  )
  return {
    entity: (row && row.entity) || null,
    groupKey: (row && row.groupKey) || null,
    inScopeLinkIds: links.map((l) => linkKeyOf(l.status)).sort(),
    platforms: entries.map((p) => p.platform),
    sharedStoreCount: links.reduce(
      (max, l) => Math.max(max, l.platform === 'BAEMIN' ? l.status.sharedStoreCount || 0 : 0),
      0
    ),
  }
}

export function matchesQuery(row, query) {
  const needle = String(query == null ? '' : query)
    .trim()
    .toLowerCase()
  return needle === '' || row.searchText.indexOf(needle) > -1
}

export function matchesGroup(row, groupKey) {
  return !groupKey || groupKey === SOLDOUT_FILTER_ALL || row.groupKey === String(groupKey)
}

/** 플랫폼 보기 — 그 배달앱에 링크가 있는 행만 남긴다. **보기만 바꾸고 대상은 고르지 않는다** (D5) */
export function matchesPlatform(row, platform) {
  if (!platform || platform === SOLDOUT_FILTER_ALL) return true
  return row.platforms.some((p) => p.platform === platform && p.status.linkState != null)
}

export function matchesStatus(row, filter) {
  switch (filter) {
    case SOLDOUT_STATUS_FILTER.SELLING:
      return !row.stopSelling
    case SOLDOUT_STATUS_FILTER.SOLD_OUT:
      return row.stopSelling
    case SOLDOUT_STATUS_FILTER.IN_PROGRESS:
      return rowInProgress(row)
    case SOLDOUT_STATUS_FILTER.ATTENTION:
      return rowNeedsAttention(row)
    default:
      return true
  }
}

/** @param filters `{query, group, platform, status}` — 없는 축은 「전체」다 */
export function filterRows(rows, filters) {
  const f = filters || {}
  return (rows || []).filter(
    (row) =>
      matchesQuery(row, f.query) &&
      matchesGroup(row, f.group) &&
      matchesPlatform(row, f.platform) &&
      matchesStatus(row, f.status)
  )
}

/** 상태 필터 버튼의 건수 — 검색·그룹·플랫폼 보기를 거친 목록에서 센다 */
export function countByStatus(rows) {
  const list = rows || []
  return Object.keys(SOLDOUT_STATUS_FILTER).reduce((acc, key) => {
    const value = SOLDOUT_STATUS_FILTER[key]
    return { ...acc, [value]: list.filter((row) => matchesStatus(row, value)).length }
  }, {})
}

/** 이미 같은 목표 상태인 건은 제외한다 (D5) — 제외한 건수는 토스트로 말한다 */
export function splitTargets(rows, stopSelling) {
  const list = rows || []
  return {
    targets: list.filter((row) => row.stopSelling !== stopSelling),
    skipped: list.filter((row) => row.stopSelling === stopSelling),
  }
}

/**
 * 확인창의 반영 범위 (D5 · 14R P55) — 대상 행들의 반영 범위([outboundScopeOf])에서: 플랫폼 합집합(처음 본 순서), 배달앱
 * 대상이 없는 건수, 배민 카탈로그를 다른 매장과 공유하는 행(**범위 안** 링크만 — 나가지 않는 링크의 공유는 이번 저장의
 * 사실이 아니다).
 *
 * 「배달앱에 나갈 곳이 전혀 없다」(`withoutTargetCount`) — 그때만 확인창이 「오더엔에만 저장됩니다」라고 말할 수 있다.
 * 정책이 꺼졌거나 그 축을 지원하지 않는 플랫폼뿐인 행은 **링크가 있어도 나가지 않으므로** 여기 든다. 「연결 필요」
 * 배지는 다른 축(`hasLink`)이다. 지목(`match`)은 어느 쪽 축에도 없다 — 옵션 쓰기는 그룹 단위라 못 지목해도 나간다
 * (§16.304②, [linkInScope]).
 */
export function scopeSummary(targets) {
  const scoped = (targets || []).map((row) => ({ row, scope: outboundScopeOf(row) }))
  return {
    platforms: scoped.reduce(
      (acc, { scope }) => scope.platforms.reduce((inner, p) => (inner.indexOf(p) < 0 ? inner.concat(p) : inner), acc),
      []
    ),
    withoutTargetCount: scoped.filter(({ scope }) => scope.platforms.length === 0).length,
    sharedRows: scoped
      .filter(({ scope }) => scope.sharedStoreCount > 0)
      .map(({ row, scope }) => ({ key: row.key, name: row.name, sharedStoreCount: scope.sharedStoreCount })),
  }
}

/** `POST /api/menu-board/stop-selling` · `opt-stop-selling` 본문 — 사유 코드는 이 화면이 정하지 않는다(`null`) */
export function stopSellingBody(row, stopSelling) {
  return row.entity === SOLDOUT_ENTITY.OPTION
    ? { smenuOptCd: row.id, stopSelling, stopReasonCd: null }
    : { smenuCd: row.id, stopSelling, stopReasonCd: null }
}

// ────────────────────────────────────────────────────────────────────
// 진행 모델 (D5 · 브리프 2026-09-04 D-F) — 판정은 **링크 입자**, 그리기는 플랫폼 단위
// ────────────────────────────────────────────────────────────────────

/** 행의 플랫폼 칸을 `{platform: entry}` 로 — 진행 판정의 after 재료(접힌 status 와 링크 집합) */
export function platformEntryMap(row) {
  return indexBy((row && row.platforms) || [], (p) => p.platform)
}

export function afterMapOf(rows) {
  return indexBy(rows || [], (row) => row.key, platformEntryMap)
}

/**
 * 저장 **전**의 head revision 스냅샷 — 진행 판정의 기준점. 입자는 **링크**다 (D-F).
 *
 * `{[row.key]: {[platform]: {scope: [linkKey], heads: {[linkKey]: revision|null}}}}` — `heads` 는 그 플랫폼 모든 링크의
 * head([revisionOf]), `scope` 는 그중 이번 저장이 나가는 링크([linkInScope])의 키([linkKeyOf])다. 옵션 축은 서버가
 * 플랫폼당 하나로 접어 내리므로 키가 플랫폼이다.
 *
 * 저장 직후의 `lastItem` 은 아직 **이전** 작업일 수 있다(계획기가 새 item 을 내기 전). 시계로 가르면 POS PC 의 시계
 * 어긋남에 흔들리므로, revision 이 스냅샷보다 **클 때** 비로소 이번 저장의 작업으로 본다([compareRevision] —
 * `itemId` 단독 비교가 아니다: 옛 intent 가 늦게 materialize 된 item 은 `itemId` 가 커도 새 작업이 아니다).
 */
export function snapshotHeads(rows) {
  return indexBy(
    rows || [],
    (row) => row.key,
    (row) =>
      indexBy(
        row.platforms || [],
        (p) => p.platform,
        (p) => ({
          scope: linksOf(p).filter(linkInScope).map(linkKeyOf),
          heads: indexBy(linksOf(p), linkKeyOf, (st) => revisionOf(st.lastItem)),
        })
      )
  )
}

/**
 * 링크 하나의 진행 판정 (D-F · 14R P54).
 *
 * **기준점은 저장 영수증의 intent 다** (`intentIds` — 이번 저장이 만든 intent 전부, 서버가 저장 응답에 내린다). 그 링크의 head
 * 가 없거나 그 intent 가 영수증의 가장 작은 intent 보다 옛것이면 `WAITING`(이번 저장의 작업이 아직 안 보인다). 더 새 intent
 * 의 item 이면 이번 것은 대체된 것이라 그 status 로 판정한다(낱말은 chip 이 말한다). 스냅샷 기준점은 「모르는 기준점」과
 * 「비어 있는 head」를 같은 `null` 로 접어, 링크 상태를 모른 채 찍은 스냅샷 위에서는 옛 작업을 새 작업으로 읽었다.
 *
 * **폴백** — 옛 서버 응답에 `intentIds` 가 없으면(`null`) 종전 스냅샷 규칙이다: revision 이 스냅샷 head 보다 클 때 새 작업
 * ([compareRevision] — `itemId` 단독 비교가 아니다). 갈래는 여기 한 곳이다(배포 순서 함정 — 서버보다 화면이 먼저 나가도
 * 판정이 서 있어야 한다).
 *
 * | 사실 | 판정 |
 * |---|---|
 * | 작업이 없거나 영수증의 intent 보다 옛것이다(폴백: revision 이 스냅샷보다 크지 않다) | `WAITING` — 이번 저장의 작업이 아직 안 보인다 |
 * | 새 작업이 진행 집합 안 | `IN_PROGRESS` |
 * | 새 작업이 `FAILED`·`OUTCOME_UNKNOWN`·`EXPIRED` | `FAILED` — 재저장이 답이다 |
 * | 새 작업이 `DRIFT_BLOCKED` | `BLOCKED` — 재저장으로 안 풀린다 (D7) |
 * | 그 밖(`SUCCEEDED`·`UNSUPPORTED`·`SUPERSEDED`·…) | `DONE` — 비진행 종결. 낱말은 chip 이 말한다 |
 *
 * @param beforeHead 스냅샷의 revision([revisionOf]) — 폴백 규칙의 기준점. `null` 이면 기준점이 없어 어떤 작업이든 새 작업이다
 * @param lastItem 지금 그 링크의 head
 * @param intentIds 저장 영수증의 intent 목록([saveReceiptOf]) — 배열이면 영수증 규칙, `null`(옛 서버)이면 폴백
 */
export function progressStateOf(beforeHead, lastItem, intentIds) {
  if (!lastItem) return PROGRESS_STATE.WAITING
  if (Array.isArray(intentIds)) {
    const floor = intentIds.length > 0 ? Math.min(...intentIds) : null
    if (floor != null && (lastItem.intentId == null || lastItem.intentId < floor)) return PROGRESS_STATE.WAITING
  } else if (beforeHead != null && compareRevision(lastItem, beforeHead) <= 0) {
    return PROGRESS_STATE.WAITING
  }
  if (isInProgressItem(lastItem)) return PROGRESS_STATE.IN_PROGRESS
  if (isFailedItem(lastItem)) return PROGRESS_STATE.FAILED
  if (isBlockedItem(lastItem)) return PROGRESS_STATE.BLOCKED
  return PROGRESS_STATE.DONE
}

/** 링크 판정들 → 플랫폼 판정 (D-F): 진행 > 대기 > 실패 > 막힘 > 완료 — 하나라도 끝나지 않았으면 끝나지 않았다 */
export function foldProgressState(states) {
  const list = states || []
  const has = (state) => list.indexOf(state) > -1
  if (has(PROGRESS_STATE.IN_PROGRESS)) return PROGRESS_STATE.IN_PROGRESS
  if (has(PROGRESS_STATE.WAITING)) return PROGRESS_STATE.WAITING
  if (has(PROGRESS_STATE.FAILED)) return PROGRESS_STATE.FAILED
  if (has(PROGRESS_STATE.BLOCKED)) return PROGRESS_STATE.BLOCKED
  return PROGRESS_STATE.DONE
}

/** 이 판정이 「기다릴 것이 없다」인가 — 진행 드로어의 종결 판정 한 곳 */
function isSettledState(state) {
  return state === PROGRESS_STATE.DONE || state === PROGRESS_STATE.FAILED || state === PROGRESS_STATE.BLOCKED
}

/**
 * 진행 드로어의 판정 (D5·D-F) — 모든 대상 플랫폼의 모든 판정 링크가 비진행 종결이면 `settled`.
 *
 * 플랫폼 한 칸의 판정 링크 = 스냅샷의 반영 범위(`scope`). 스냅샷에 범위가 없는 플랫폼(옵션 축에서 서버가 응답으로
 * 넓힌 플랫폼)은 스냅샷이 아는 링크 전부, 그것도 없으면 플랫폼 키 하나 — 지금 행에서 그 키의 링크를 찾지 못하면
 * `WAITING` 이다(after 없음 → 대기). 플랫폼 판정은 [foldProgressState], 드로어는 플랫폼 단위로 그린다(`status` 는
 * 그 칸의 접힌 status — chip 재료).
 *
 * @param targets `[{key, platforms: [platformCode], intentIds, saveOk, saveMessage}]` — `platforms` 는 이번 요청의 반영
 *   범위(메뉴 = `inScope` 플랫폼 · 옵션 = 저장 응답의 `deliveryPlatforms[]` · 영수증이 「아무것도 안 나갔다」면 빈 목록,
 *   [saveReceiptOf]), `intentIds` 는 저장 영수증의 intent(옛 서버면 `null` — 스냅샷 규칙 폴백, [progressStateOf]).
 *   오더엔 저장이 실패한 건(`saveOk === false`)은 기다릴 것이 없어 종결이고 실패다. 대상 플랫폼이 없는 건도 종결이다
 *   (오더엔에만 저장됨).
 * @param before [snapshotHeads] — 판정 링크의 집합과 폴백 규칙의 기준점
 * @param after [afterMapOf] — 최신 행
 */
export function judgeProgress(targets, before, after) {
  const rows = (targets || []).map((target) => {
    if (target.saveOk === false) {
      return {
        key: target.key,
        saveOk: false,
        saveMessage: target.saveMessage || null,
        platforms: [],
        settled: true,
        failed: true,
        blocked: false,
      }
    }
    const beforeRow = (before && before[target.key]) || {}
    const afterRow = (after && after[target.key]) || {}
    const platforms = (target.platforms || []).map((platform) => {
      const snap = beforeRow[platform] || { scope: [], heads: {} }
      const entry = afterRow[platform] || null
      const known = Object.keys(snap.heads)
      const keys = snap.scope.length > 0 ? snap.scope : known.length > 0 ? known : [platform]
      const afterByKey = indexBy(linksOf(entry), linkKeyOf)
      const links = keys.map((key) => {
        const status = afterByKey[key] || null
        return {
          key,
          status,
          state: progressStateOf(snap.heads[key] || null, status && status.lastItem, target.intentIds),
        }
      })
      return {
        platform,
        status: entry ? entry.status : null,
        links,
        state: foldProgressState(links.map((l) => l.state)),
      }
    })
    return {
      key: target.key,
      saveOk: true,
      saveMessage: null,
      platforms,
      settled: platforms.every((p) => isSettledState(p.state)),
      failed: platforms.some((p) => p.state === PROGRESS_STATE.FAILED),
      blocked: platforms.some((p) => p.state === PROGRESS_STATE.BLOCKED),
    }
  })
  return {
    rows,
    settled: rows.every((r) => r.settled),
    failed: rows.some((r) => r.failed),
    blocked: rows.some((r) => r.blocked),
    failedPlatformCount: rows.reduce(
      (n, r) => n + r.platforms.filter((p) => p.state === PROGRESS_STATE.FAILED).length,
      0
    ),
    blockedPlatformCount: rows.reduce(
      (n, r) => n + r.platforms.filter((p) => p.state === PROGRESS_STATE.BLOCKED).length,
      0
    ),
    saveFailedCount: rows.filter((r) => r.saveOk === false).length,
  }
}

// ────────────────────────────────────────────────────────────────────
// 진행 회차 = 「보낸 사실」 + 파생 (11R P1)
//
// 저장하는 것은 사실뿐이다 — 어느 묶음을 언제 보냈고 그 직전 head 가 무엇이었나. 판정([judgeProgress])·국면
// ([progressPhaseOf])·「방금 보냈다」 잠금([pendingSaveKeysOf])·재저장 묶음([reapplyBatchesOf])은 전부 그 사실과
// **지금 목록**에서 파생한다. 판정을 저장해 두고 합류 때 손으로 이어 붙이던 8R~10R 의 모델은 키가 재사용될 때 옛
// 판정이 남고, 묶음의 정체(축·값)가 대상에 흩어져 재저장이 엔드포인트를 잘못 고르는 결함을 냈다(11R 리뷰) —
// 수정에 대한 수정이 네 겹이면 모델이 틀린 것이라 규칙이 아니라 모델을 바꿨다.
//
// ```
// run = {
//   batches:  [{ batchId, entity, stopSelling, keys: [rowKey] }],   // 보낸 묶음 — 정체(축·값)가 묶음에 있다
//   targets:  { [rowKey]: { key, id, name, entity, stopSelling, platforms, intentIds, saveOk, saveMessage, batchId } },
//                                                                  ↑ 저장 영수증 — 판정의 기준점 (14R P54)
//   before:   { [rowKey]: snapshotHeads 의 한 행 },                   // 보내기 직전 head (D-F)
//   startedAt, checkedAt,                                            // 폴링 시작 · 마지막 확인 시각
//   saving:   { index, total } | null,                               // POST 루프 중일 때만
//   terminal: null | RUN_TERMINAL.AUTH,                              // 401/403 으로 끝났다 (P4)
//   concludedAt: null | ms,                                           // 화면이 확인을 처음 끝낸 시각(종결·시간초과) — 신호는 한 번 (13R P35)
// }
// ```
// ────────────────────────────────────────────────────────────────────

/** 회차가 끝난 이유 — 지금은 인증 실패 하나 (P4). 세션이 끝난 회차는 다시 확인하지도 재저장하지도 않는다 */
export const RUN_TERMINAL = Object.freeze({ AUTH: 'auth' })

/**
 * 보내기 직전, 확인한 키를 **지금 목록의 행**으로 다시 해석한다 (P1 `dispatchSave` ②).
 *
 * 확인창이 열린 사이 폴링이 목록을 갈아 끼우면 옛 행 객체로 스냅샷을 찍게 되므로, 확인창은 키만 넘기고 여기서 행을 다시
 * 찾는다. 그 사이 고를 수 없게 된 행(진행 중·방금 보냄·상태 모름·사라짐·축이 다름)이 하나라도 있으면 `changed` —
 * 화면은 보내지 않고 다시 확인하라고 말한다(`T.TARGETS_CHANGED`). 목표 값은 비교하지 않는다 — 재저장은 같은 값을 다시
 * 보내는 것이 뜻이다.
 *
 * ★ **지문을 대조한다** (13R P28). 키·축·선택 가능 여부만 다시 보면 확인창이 열린 사이 목표 값·정책·링크·capability·
 * 공유 매장이 바뀌어도 `changed:false` 로 보낸다(사용자가 보지 못한 범위로 전파, 이미 목표 상태인 행에 불필요한 새 작업).
 * 확인 시점의 지문([fingerprintsOf])을 받아 같은 함수로 지금 지문을 만들고 **하나라도 다르면** `changed` 다.
 * `seen` 이 없으면(`null`) 지문은 대조하지 않는다 — 화면은 언제나 넘긴다.
 *
 * @param batches `[{entity, stopSelling, keys}]`
 * @param pendingKeys 「방금 보냈다」 잠금 — 볼지 말지는 묶음을 만든 쪽의 규칙이다(14R P52 · [reapplyBatchesOf] 의 `ignorePending`)
 * @param seen 확인 시점의 지문 `{[key]: fingerprint}` ([fingerprintsOf])
 * @param opts [rowSelectable] 의 `opts` — 되읽는 중이면 어느 행도 보낼 수 없다(14R P46)
 * @returns `{batches: [{entity, stopSelling, rows}], changed, fingerprints}` — 빈 묶음은 뺀다, `fingerprints` 는 지금 지문
 */
export function resolveSaveBatches(batches, rowsByKey, pendingKeys, seen, opts) {
  const resolved = (batches || []).map((b) => ({
    entity: b.entity,
    stopSelling: b.stopSelling,
    rows: (b.keys || [])
      .map((key) => (rowsByKey || {})[key])
      .filter((row) => row && row.entity === b.entity && rowSelectable(row, pendingKeys, opts)),
  }))
  const fingerprints = indexBy(
    resolved.reduce((acc, b) => acc.concat(b.rows.map((row) => saveFingerprintOf(row, b.stopSelling))), []),
    (fp) => fp.key
  )
  const wanted = (batches || []).reduce((n, b) => n + (b.keys || []).length, 0)
  const got = resolved.reduce((n, b) => n + b.rows.length, 0)
  const drifted =
    seen != null && Object.keys(fingerprints).some((key) => !sameFingerprint(seen[key], fingerprints[key]))
  return { batches: resolved.filter((b) => b.rows.length > 0), changed: got !== wanted || drifted, fingerprints }
}

/**
 * **확인 지문** (13R P28 · 14R P55) — 확인창(또는 실패 카드)이 보여 준 행의 사실: 보낼 값(`stopSelling`, [reapplyValueOf] 와
 * 같은 파생 규칙), 행의 지금 값(`current` — 확인창이 「현재 상태」로 적는 것), 그리고 반영 범위의 정본([outboundScopeOf] —
 * 축·옵션 그룹·범위 안 링크 id·플랫폼·범위 안 공유 매장 수). 보내기 직전에 같은 함수로 다시 만들어 대조한다
 * ([resolveSaveBatches]). 링크 id 가 드는 이유: 같은 플랫폼·같은 수라도 링크가 갈아 끼워졌으면 사용자가 본 범위가 아니다.
 */
export function saveFingerprintOf(row, stopSelling) {
  const scope = outboundScopeOf(row)
  return {
    key: row.key,
    stopSelling: !!stopSelling,
    current: !!row.stopSelling,
    entity: scope.entity,
    groupKey: scope.groupKey,
    inScopeLinkIds: scope.inScopeLinkIds,
    inScopePlatforms: scope.platforms.slice().sort(),
    sharedStoreCount: scope.sharedStoreCount,
  }
}

function sameList(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x, i) => x === b[i])
}

export function sameFingerprint(a, b) {
  if (!a || !b) return false
  return (
    a.key === b.key &&
    a.stopSelling === b.stopSelling &&
    a.current === b.current &&
    a.entity === b.entity &&
    a.groupKey === b.groupKey &&
    a.sharedStoreCount === b.sharedStoreCount &&
    sameList(a.inScopeLinkIds, b.inScopeLinkIds) &&
    sameList(a.inScopePlatforms, b.inScopePlatforms)
  )
}

/** 묶음(`[{stopSelling, keys}]`)의 키마다 지문 — 지금 목록의 행으로. 없는 키는 뺀다 */
export function fingerprintsOf(batches, rowsByKey) {
  const acc = {}
  ;(batches || []).forEach((b) => {
    ;(b.keys || []).forEach((key) => {
      const row = (rowsByKey || {})[key]
      if (row) acc[key] = saveFingerprintOf(row, b.stopSelling)
    })
  })
  return acc
}

/**
 * 보내기 직전의 회차 — 거르기를 마친 묶음(행 객체 포함, [resolveSaveBatches])에서 사실을 찍는다.
 * 대상 플랫폼은 반영 범위의 정본([outboundScopeOf])에서 오고, 저장 영수증([saveReceiptOf])이 뒤에 덮는다(옵션 = 응답의
 * `deliveryPlatforms[]` · 빈 영수증 = 빈 목록). `intentIds` 는 영수증이 오기 전이라 `null` 이다.
 */
export function runOf(batches, now) {
  const stamped = (batches || []).map((b, i) => ({
    batchId: `${b.entity}:${b.stopSelling}:${now}:${i}`,
    entity: b.entity,
    stopSelling: b.stopSelling,
    keys: b.rows.map((row) => row.key),
  }))
  const targets = {}
  const before = {}
  ;(batches || []).forEach((b, i) => {
    b.rows.forEach((row) => {
      targets[row.key] = {
        key: row.key,
        id: row.id,
        name: row.name,
        entity: row.entity,
        stopSelling: b.stopSelling,
        platforms: outboundScopeOf(row).platforms,
        intentIds: null,
        saveOk: null,
        saveMessage: null,
        batchId: stamped[i].batchId,
      }
    })
    Object.assign(before, snapshotHeads(b.rows))
  })
  return {
    batches: stamped,
    targets,
    before,
    startedAt: now,
    checkedAt: null,
    saving: null,
    terminal: null,
    concludedAt: null,
  }
}

/**
 * **저장 영수증 → 대상의 사실** (14R P54). 저장 응답의 `intentIds`(이번 저장이 만든 intent 전부)를 싣는다 — 옛 서버(없음)는
 * `null` 이라 판정이 스냅샷 규칙으로 폴백한다([progressStateOf]). **빈 배열은 아무것도 나가지 않았다는 사실**이라 대상
 * 플랫폼을 비운다(오더엔에만 — 즉시 종결, 드로어의 「반영 대상 없음」). 옵션 축은 응답의 `deliveryPlatforms[]` 가 범위를
 * 덮는다(서버가 확정한 범위). 응답 본문이 객체가 아니면(옛 메뉴 저장은 빈 문자열) 영수증이 없는 것이다.
 */
export function saveReceiptOf(target, data) {
  const body = data && typeof data === 'object' ? data : {}
  const intentIds = Array.isArray(body.intentIds) ? body.intentIds.map(Number) : null
  const responded =
    target.entity === SOLDOUT_ENTITY.OPTION && Array.isArray(body.deliveryPlatforms) ? body.deliveryPlatforms : null
  return {
    ...target,
    saveOk: true,
    saveMessage: null,
    intentIds,
    platforms: intentIds && intentIds.length === 0 ? [] : responded || target.platforms,
  }
}

/** 회차의 대상 목록 — 묶음 순서대로. 판정([judgeProgress])과 드로어의 입력이다 */
export function runTargets(run) {
  const targets = (run && run.targets) || {}
  return ((run && run.batches) || []).reduce(
    (acc, b) => acc.concat((b.keys || []).map((key) => targets[key]).filter(Boolean)),
    []
  )
}

/** 회차가 걸친 축(`SOLDOUT_ENTITY`)들 — 폴링이 어느 상태 조회를 되읽을지, 끝난 뒤 무엇을 다시 읽을지 */
export function progressEntities(run) {
  return ((run && run.batches) || []).reduce((acc, b) => (acc.indexOf(b.entity) < 0 ? acc.concat(b.entity) : acc), [])
}

/**
 * **새 저장은 살아 있는 회차를 대체하지 않고 합류한다** (10R R14 → 11R P1).
 *
 * `batches` 는 이어 붙이고, `targets`·`before` 는 새 키가 **덮는다** — 키 재사용(다른 대상이 회차를 살려 둔 사이 종결된
 * 행을 다시 저장)은 새 사실이라 옛 묶음에서 그 키를 뺀다. 판정은 파생이라 손댈 것이 없고, 그래서 옛 판정이 남지 않는다.
 * `startedAt` 은 새 묶음의 것(상한은 「사람이 서서 보는 시간」이라 합류 시점부터 다시 센다), `checkedAt` 은 유지.
 */
export function mergeRuns(live, incoming) {
  const incomingKeys = (incoming.batches || []).reduce((acc, b) => acc.concat(b.keys || []), [])
  return {
    ...live,
    batches: (live.batches || [])
      .map((b) => ({ ...b, keys: (b.keys || []).filter((key) => incomingKeys.indexOf(key) < 0) }))
      .filter((b) => b.keys.length > 0)
      .concat(incoming.batches || []),
    targets: { ...(live.targets || {}), ...(incoming.targets || {}) },
    before: { ...(live.before || {}), ...(incoming.before || {}) },
    startedAt: incoming.startedAt,
    saving: incoming.saving,
    terminal: incoming.terminal,
    concludedAt: incoming.concludedAt,
  }
}

/**
 * 회차의 국면 — 사실([run])과 파생된 판정에서. `now` 는 마지막 확인 시각(`run.checkedAt`)이다: 상한 판정은 확인한 시점의
 * 사실이라, 확인이 없었으면(`null`) 아직 넘기지 않은 것이다. 세션이 끝난 회차([RUN_TERMINAL])는 무엇보다 먼저다.
 */
export function progressPhaseOf(run, judged, now) {
  if (!run) return null
  if (run.terminal) return PROGRESS_PHASE.TERMINAL
  if (run.saving) return PROGRESS_PHASE.SAVING
  if (judged && judged.settled) return PROGRESS_PHASE.SETTLED
  if (now != null && progressTimedOut(run.startedAt, now)) return PROGRESS_PHASE.TIMEOUT
  return PROGRESS_PHASE.POLLING
}

/** 확인이 도는 중인가(`saving`·`polling`) — 드로어의 실패·막힘 카드가 숨는 창이자 「방금 보냈다」가 사실인 유일한 창 */
export function progressLive(run, judged, now) {
  const phase = progressPhaseOf(run, judged, now)
  return phase === PROGRESS_PHASE.SAVING || phase === PROGRESS_PHASE.POLLING
}

/**
 * **저장은 갔는데 아직 새 작업이 안 보이는 행들** — [rowAwaitingNewItem] 의 재료 (9R R5 · 11R 파생).
 *
 * 확인이 도는 동안([progressLive])에만 값을 갖고, 종결·시간초과·세션 종료 뒤에는 빈 배열이다 — 시간초과는 규약이 정상
 * 경로로 못박은 값이라 그 뒤에도 잠가 두면 창을 다시 열기 전까지 영영 못 누른다(멈춘 뒤에는 「모른다」이고 다시 누르는
 * 것은 사장님의 권리다). **저장 중(`run.saving`)에는 보낸 사실이 곧 잠금이라 대상 전부**(반영 대상 플랫폼이 없어 판정이
 * 이미 종결인 행도 POST 가 도는 동안은 다시 누를 수 없다 — 12R P25), 그 뒤에는 판정이 `WAITING` 인 대상만이다.
 * 판정은 회차의 대상 전부를 덮으므로(`judgeProgress(runTargets(run), …)`) 「판정에 없는 대상」이라는 갈래는 없다.
 */
export function pendingSaveKeysOf(run, judged, now) {
  if (!progressLive(run, judged, now)) return []
  const targets = runTargets(run)
  if (run.saving) return targets.map((t) => t.key)
  const judgedByKey = indexBy((judged && judged.rows) || [], (r) => r.key)
  return targets
    .filter((t) => {
      const row = judgedByKey[t.key]
      return !!row && (row.platforms || []).some((pl) => pl.state === PROGRESS_STATE.WAITING)
    })
    .map((t) => t.key)
}

/**
 * 「현재 값 다시 저장」의 묶음 — 실패한 행을 **`(entity, stopSelling)`** 로 묶는다 (D7 · 9R R5 보강 · 11R 합의 2 · 12R P11).
 *
 * **값의 출처는 실패의 성격이 정한다** (13R P27′ · 합의 3 — [reapplyValueOf]): 배달앱 반영 실패(`saveOk===true`)는 행의
 * 지금 값(D7 「현재 값」 — 실패 뒤 새로고침·다른 조작자로 바뀌었으면 그 값), 오더엔 저장 실패(`saveOk===false`)는 행이
 * 옛 값 그대로라 **보냈던 의도**(`targets[key].stopSelling`) — 지금 값으로 보내면 반대 값이 나간다. 같은 값의 재전송은 서버
 * `ALREADY_IN_SYNC` 가 흡수하므로 첫 요청이 실제로 커밋됐어도 안전하고, 사장님이 다시 고를 필요가 없다. 묶음마다
 * 엔드포인트가 자명하다(메뉴·옵션이 한 묶음에 섞이지 않는다). **「방금 보냈다」 축은 보지 않는다** — 재저장은 그 잠금을
 * 푸는 행위다(`rowSelectable(row, [])`). 상태를 모르는 행·진행 중인 행·세션이 끝난 회차는 빠진다.
 *
 * ★ 그 규칙은 **값으로 돌려준다** (14R P52 — `ignorePending:true`). 보내는 쪽(`dispatchSave` → [resolveSaveBatches])이 같은
 * 잠금을 제 판단으로 다시 적용하면 규칙이 둘이 된다 — 묶음을 만든 쪽이 정한 것을 보내는 쪽이 따른다.
 *
 * @returns `{batches: [{entity, stopSelling, keys}], ignorePending: true}` — `batches` 는 [resolveSaveBatches] 의 입력 모양
 */
export function reapplyBatchesOf(run, judged, rowsByKey) {
  const targets = (run && run.targets) || {}
  const failedKeys =
    !run || run.terminal || !judged ? [] : (judged.rows || []).filter((r) => r.failed).map((r) => r.key)
  const batches = failedKeys.reduce((acc, key) => {
    const target = targets[key]
    const row = (rowsByKey || {})[key]
    if (!target || !rowSelectable(row, [])) return acc
    const entity = row.entity
    const stopSelling = reapplyValueOf(target, row)
    const found = acc.find((b) => b.entity === entity && b.stopSelling === stopSelling)
    return found
      ? acc.map((b) => (b === found ? { ...b, keys: b.keys.concat(key) } : b))
      : acc.concat({ entity, stopSelling, keys: [key] })
  }, [])
  return { batches, ignorePending: true }
}

/**
 * 재저장 값의 출처 (13R P27′) — 오더엔에 닿지 못한 저장(`saveOk===false`)은 보냈던 의도, 닿은 저장(배달앱 반영 실패)은
 * 행의 지금 값. 확인 지문([saveFingerprintOf])의 「보낼 값」도 같은 규칙이다.
 */
export function reapplyValueOf(target, row) {
  return target && target.saveOk === false ? !!target.stopSelling : !!(row && row.stopSelling)
}

/**
 * 보내기 루프 뒤의 요약 — 이번에 보낸 키들 중 오더엔 저장 성공·실패 수 (12R P15 · 원장 `SAVE-ALL-FAILED-GREEN-TOAST`).
 * 성공이 하나도 없으면 초록 「저장을 마쳤습니다」를 띄우지 않는다 — 문구는 기존 SSOT(`posSoldoutFailureTitle` ·
 * `SAVED_CHECKING`)를 조합할 뿐 새로 만들지 않는다.
 */
export function saveOutcomeOf(run, keys) {
  const targets = (run && run.targets) || {}
  return (keys || []).reduce(
    (acc, key) => {
      const t = targets[key]
      if (!t) return acc
      if (t.saveOk === true) return { ...acc, succeeded: acc.succeeded + 1 }
      if (t.saveOk === false) return { ...acc, failed: acc.failed + 1 }
      return acc
    },
    { succeeded: 0, failed: 0 }
  )
}

/** 저장 요약의 갈래 (13R P34) — 전건 실패 · 부분 실패 · 전건 성공 · (보낸 것 없음) */
export const SAVE_OUTCOME = Object.freeze({ NONE: 'none', FAILED: 'failed', PARTIAL: 'partial', OK: 'ok' })

export function saveOutcomeKind(outcome) {
  const succeeded = (outcome && outcome.succeeded) || 0
  const failed = (outcome && outcome.failed) || 0
  if (succeeded === 0 && failed === 0) return SAVE_OUTCOME.NONE
  if (succeeded === 0) return SAVE_OUTCOME.FAILED
  return failed > 0 ? SAVE_OUTCOME.PARTIAL : SAVE_OUTCOME.OK
}

/**
 * 세션 매장 + 형제 매장 → 스코프 (D1 · 13R P30/P31 · 14R P48). 세션 매장이 앞이고 형제 목록에 섞인 세션 매장은 뺀다.
 * 이름 사전은 [indexBy] — 같은 매장이 두 번 오면 뒤 값이 이긴다.
 *
 * 형제 조회에 **실패했으면 `siblings` 는 `null`** 이다(스코프를 모르는 것이지 「형제 없음」이 아니다). 그때 모르는 것은
 * **소속**(`storeCds:null` — [storeInScope] 가 `null` 을 내어 서버 판정에 맡긴다)뿐이고 세션 매장의 이름은 안다 — 스코프를
 * 통째로 `null` 로 접으면 머리글이 「매장 #13」이 된다. 빈 배열은 「형제 없음」을 **아는** 것이다.
 *
 * @returns `{sessionStoreCd, storeNm, storeCds: number[]|null, names}`
 */
export function sessionScopeOf(sessionStoreCd, sessionStoreNm, siblings) {
  const storeNm = sessionStoreNm || ''
  const members = [{ storeCd: sessionStoreCd, storeNm }].concat(
    (siblings || []).filter((s) => s.storeCd !== sessionStoreCd)
  )
  return {
    sessionStoreCd,
    storeNm,
    storeCds: Array.isArray(siblings) ? members.map((m) => m.storeCd) : null,
    names: indexBy(
      members,
      (m) => m.storeCd,
      (m) => m.storeNm
    ),
  }
}

/**
 * 읽기 조정자의 세대 검사 (P3) — `await` 뒤에 세대가 넘어갔으면 그 결과는 옛 세계의 것이라 **커밋하지 않는다**.
 * 로더는 결과만 돌려주고, 커밋은 이 검사를 지난 뒤에만 한다 — 옛 응답이 새 값을 덮는 경로가 구조적으로 없다.
 * 같은 세대 안의 동시 읽기(수동 새로고침이 폴과 겹침)는 축마다의 **요청 순번**에 같은 술어를 건다 — 최신 순번의 응답만
 * 커밋한다(latest-wins, 12R P13).
 */
export function isStaleRead(startedEpoch, currentEpoch) {
  return startedEpoch !== currentEpoch
}

/**
 * **읽기 트랜잭션의 낡음** (14R P42 보강) — 조정자가 첫 I/O 전에 세대와 걸친 축의 순번을 전부 예약한 값(`txn = {gen, seq}`)을
 * `await` 뒤 지금 값과 견준다: 세대가 넘어갔거나 예약한 축 중 하나라도 순번이 넘어갔으면 그 트랜잭션의 결과는 커밋하지
 * 않는다. 전체 되읽기와 폴 체인이 같은 규칙을 쓰므로, 한쪽이 뒤늦게 다른 축의 순번을 올려 상대의 원자 커밋을 버리게 하는
 * 길이 없다(예약이 첫 I/O 전이라 뒤늦은 올림 자체가 없다). 예약하지 않은 축은 보지 않는다.
 */
export function isStaleTxn(txn, gen, readSeq) {
  const seq = (txn && txn.seq) || {}
  return (
    isStaleRead(txn && txn.gen, gen) || Object.keys(seq).some((axis) => isStaleRead(seq[axis], (readSeq || {})[axis]))
  )
}

export function progressTimedOut(startedAt, now, timeoutMs) {
  const limit = typeof timeoutMs === 'number' ? timeoutMs : POLL_WATCH_CAP_MS
  return now - startedAt >= limit
}

/** 관측 시각 둘 중 최신 — 읽을 수 없는 값은 없는 것으로 본다. 링크·플랫폼·목록 세 층이 같은 비교를 쓴다 */
function newerObservedAt(acc, candidate) {
  if (!candidate) return acc
  const at = new Date(candidate)
  if (Number.isNaN(at.getTime())) return acc
  return acc == null || at.getTime() > new Date(acc).getTime() ? candidate : acc
}

/** 링크 하나의 관측 시각 — 메뉴 축은 `drift.observedAt`, 옵션 축은 `observed.observedAt` */
function linkObservedAt(status) {
  return (status.drift && status.drift.observedAt) || (status.observed && status.observed.observedAt) || null
}

/** 플랫폼 한 칸의 관측 시각 — 링크들 중 최신 (D-E) */
function newestLinkObservedAt(links) {
  return (links || []).reduce((acc, st) => newerObservedAt(acc, linkObservedAt(st)), null)
}

/** 목록에서 가장 최근의 관측 시각 — 머리 통계 「배달앱 관측」. 없으면 `null` */
export function newestObservedAt(rows) {
  return (rows || []).reduce(
    (newest, row) => (row.platforms || []).reduce((acc, p) => newerObservedAt(acc, p.observedAt), newest),
    null
  )
}

/** 관측 스냅샷의 판매 상태 낱말 — 문장은 화면이 `POS_SOLDOUT_TEXT.OBSERVED_*` 로 붙인다 */
export function observedSoldOutState(observed) {
  if (!observed) return 'unknown'
  if (observed.hidden === true) return 'hidden'
  if (observed.soldOut === true) return 'soldout'
  if (observed.soldOut === false) return 'selling'
  return 'unknown'
}

/** 쿼리 `storeCd` — 양의 정수만. 배열(중복 쿼리)은 첫 값 */
export function parseStoreCd(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw
  if (value == null) return null
  const text = String(value).trim()
  if (!/^\d+$/.test(text)) return null
  const n = Number(text)
  return n > 0 ? n : null
}

/**
 * 쿼리 매장이 세션 스코프 안인가 (D1). 스코프를 모르면(`null` — 매장 계정이 아니거나 조회 실패) `null` 이고 그때는
 * 서버 판정(403)에 맡긴다. **세션 첫 매장으로 몰래 대체하지 않는다.**
 */
export function storeInScope(storeCd, scopeStoreCds) {
  if (!Array.isArray(scopeStoreCds)) return null
  return scopeStoreCds.some((cd) => Number(cd) === Number(storeCd))
}

/**
 * **매장 셀렉터의 모델** (2026-09-05 D-S3′ — 정정판) — 진입점을 가리지 않는다. 순수 함수라 화면은 그리기만 한다.
 *
 * 초판은 임베드에서 이 셀렉터를 숨겼다. 근거는 pos2-lite 의 `PosStoreSwitcher` 가 매장 축의 유일한 주인이라는
 * 것이었는데, 그 스위처는 `PosAppBar` 안에 있고 품절 화면으로 가는 두 동선(메인 허브 카드·통합주문관리 버튼)에는
 * `PosAppBar` 가 없다 — **축이 겹치지 않는다.** 숨기면 사장님이 형제 매장 중 어느 가게를 품절시키는지 모른 채
 * 누르게 되므로, 두 진입점 모두 그린다. `storeCd` 해석의 갈림(D-S2)은 진입점의 계약이라 이것과 별개다.
 *
 * 후보는 [sessionScopeOf] 가 이미 만들어 둔 것뿐이다(세션 매장이 맨 앞) — **조회를 새로 하지 않는다.**
 * 셀렉터를 여는 것(`switchable`)은 **고를 것이 둘 이상일 때**뿐이다. 그룹이 한 곳뿐이거나 형제 조회에
 * 실패했으면(`storeCds:null`) 고를 것이 없어 화면은 이름만 그린다 — 없는 드롭다운을 열지 않는다.
 *
 * `isSibling` 은 **아는 때만** 참이다 — 세션 매장을 모르면(매장 계정이 아님) 형제라고 단정하지 않는다.
 *
 * @returns `{choices: [{storeCd, name, isSession}], isSibling, switchable, locked}`
 */
export function storeBarOf(scope, storeCd, busy) {
  const sessionCd = scope && scope.sessionStoreCd != null ? Number(scope.sessionStoreCd) : null
  const cds = Array.isArray(scope && scope.storeCds) ? scope.storeCds.map(Number) : []
  const current = storeCd == null ? null : Number(storeCd)
  return {
    choices: cds.map((cd) => ({ storeCd: cd, name: (scope && scope.names[cd]) || '', isSession: cd === sessionCd })),
    isSibling: sessionCd != null && current != null && current !== sessionCd,
    switchable: cds.length > 1,
    /**
     * **형제 목록을 못 읽었다** — 「형제가 없다」와 같은 화면으로 접지 않는다 (13R P30 · 원장
     * `EMBED-SIBLING-LOOKUP-FAIL-AS-EMPTY` 와 같은 편). 둘 다 고를 것이 없어 링크는 서지 않지만,
     * 이쪽은 **모른다**는 사실을 화면이 말해야 사장님이 다시 열어 볼 수 있다.
     * `scope` 자체가 없는 것(매장 계정이 아님)은 여기서 말할 일이 아니다 — 그 자리는 `notice` 가 답한다.
     */
    siblingsUnread: scope != null && scope.storeCds === null,
    /** 저장·반영 확인이 도는 동안에는 잠근다 (D-S5) — 보낸 회차의 기준점이 다른 매장 목록 위에 서게 된다 */
    locked: busy === true,
  }
}

/**
 * **매장이 바뀔 때 버리는 것** (2026-09-05 D-S4) — 옛 매장의 사실은 새 매장 화면에서 전부 거짓이다.
 *
 * 맨 앞의 `selectedKeys` 가 이 회차의 안전장치다: 남은 선택이 다음 매장으로 넘어가면 사장님이 엉뚱한 가게를
 * 품절시킨다. 목록·스냅샷·오류·진행 회차·필터(그룹 필터는 옛 매장의 `smenuOptTpCd` 다)가 함께 간다. 읽기 세대를
 * 올리는 것(`stopPolling`)은 경계의 일이라 여기 없다 — 이 함수는 **버릴 상태의 목록**만 소유한다.
 */
export function storeScopedReset() {
  return {
    selectedKeys: [],
    menus: [],
    menuStatus: {},
    optionGroups: [],
    menuError: null,
    optionError: null,
    transportError: null,
    loadForbidden: false,
    run: null,
    reapplySeen: null,
    progressVisible: false,
    detailVisible: false,
    detailSmenuCd: null,
    bindVisible: false,
    bindLink: null,
    optionDetailVisible: false,
    optionDetailKey: null,
    optionMappingEntry: null,
    optionMappingBusy: false,
    query: '',
    groupFilter: SOLDOUT_FILTER_ALL,
    statusFilter: SOLDOUT_STATUS_FILTER.ALL,
    platformFilter: SOLDOUT_FILTER_ALL,
  }
}
